import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { TitleTable } from "components/TitleTable";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { apiAntigo } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`liberacao-tabpanel-${index}`}
      aria-labelledby={`liberacao-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface IMaterialLiberado {
  id: number;
  tipo: string;
  referencia: string;
  linha: string;
  data: string;
}

export const MateriaisLiberadosDiversa = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openNotification } = useNavigation();

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [liberacaoInfo, setLiberacaoInfo] = useState<any>(null);
  
  // Materiais N1 (Produtos)
  const [materiaisN1, setMateriaisN1] = useState<IMaterialLiberado[]>([]);
  
  // Materiais N2 (Tipologias)
  const [materiaisN2, setMateriaisN2] = useState<IMaterialLiberado[]>([]);

  // Modal de exclusão
  const [modalDelete, setModalDelete] = useState<{
    isOpen: boolean;
    materialId?: number;
    tipo: "n1" | "n2";
  }>({ isOpen: false, tipo: "n1" });

  useEffect(() => {
    if (id) {
      carregarDadosLiberacao();
      carregarMateriaisN1();
      carregarMateriaisN2();
    }
  }, [id]);

  const carregarDadosLiberacao = async () => {
    try {
      const { data } = await apiAntigo.get(`/LiberacaoDiversa/Liberacao/${id}`);
      setLiberacaoInfo(data);
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    }
  };

  const carregarMateriaisN1 = async () => {
    setIsLoading(true);
    try {
      // Buscar materiais N1 (Produtos) liberados
      const { data } = await apiAntigo.get(`/LiberacaoDiversa/LiberacaoPortfolioDisponivel/${id}/1`);
      
      // Mock - Você precisará ajustar conforme estrutura real da API
      setMateriaisN1([]);
    } catch (error: any) {
      console.error("Erro ao carregar materiais N1:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const carregarMateriaisN2 = async () => {
    setIsLoading(true);
    try {
      // Buscar materiais N2 (Tipologias) liberados
      const { data } = await apiAntigo.get(`/LiberacaoDiversa/LiberacaoTipologiaDisponivel/${id}`);
      
      // Mock - Você precisará ajustar conforme estrutura real da API
      setMateriaisN2([]);
    } catch (error: any) {
      console.error("Erro ao carregar materiais N2:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverMaterial = async () => {
    if (!modalDelete.materialId) return;

    setIsLoading(true);
    try {
      // Implementar lógica de remoção
      if (modalDelete.tipo === "n1") {
        // Remover de N1
        await carregarMateriaisN1();
      } else {
        // Remover de N2
        await carregarMateriaisN2();
      }

      openNotification({
        message: "Material removido com sucesso!",
        type: "success",
      });
      setModalDelete({ isOpen: false, tipo: "n1" });
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdicionarMateriais = () => {
    const nivel = activeTab === 0 ? "n1" : "n2";
    navigate(`/liberacoesDiversas/${id}/materiais/adicionar?nivel=${nivel}`);
  };

  const columnsN1: GridColDef[] = [
    {
      field: "tipo",
      headerName: "Tipo de Pacote",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "referencia",
      headerName: "Referência",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "linha",
      headerName: "Linha",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "data",
      headerName: "Data",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          title="Remover"
          onClick={() =>
            setModalDelete({
              isOpen: true,
              materialId: params.row.id,
              tipo: "n1",
            })
          }
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const columnsN2: GridColDef[] = [
    {
      field: "projetista",
      headerName: "Projetista",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "linha",
      headerName: "Linha/Sistema",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "data",
      headerName: "Data",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          title="Remover"
          onClick={() =>
            setModalDelete({
              isOpen: true,
              materialId: params.row.id,
              tipo: "n2",
            })
          }
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title={`LIBERAÇÕES > ${liberacaoInfo?.empresa || "EMPRESA"}`}
        label="Materiais liberados para o cliente"
        hasBackButton={true}
        goBack={() => navigate("/liberacoesDiversas")}
      />

      <Paper elevation={3}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ px: 2 }}
          >
            <Tab label="CADASTRO N1" id="materiais-tab-0" />
            <Tab label="CADASTRO N2" id="materiais-tab-1" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <TitleTable title="Lista de materiais liberados para CEM PRO (CEM30-00004-000A)" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdicionarMateriais}
            >
              ADICIONAR
            </Button>
          </Box>

          <StripedDataGrid
            columns={columnsN1}
            rows={materiaisN1}
            getRowId={(row) => row.id}
            loading={isLoading}
            autoHeight
            checkboxSelection={false}
          />

          {materiaisN1.length === 0 && !isLoading && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Nenhum material liberado. Clique em "ADICIONAR" para liberar materiais.
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <TitleTable title="Lista de materiais liberados para CEM PRO (CEM30-00004-000A)" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdicionarMateriais}
            >
              ADICIONAR
            </Button>
          </Box>

          <StripedDataGrid
            columns={columnsN2}
            rows={materiaisN2}
            getRowId={(row) => row.id}
            loading={isLoading}
            autoHeight
            checkboxSelection={false}
          />

          {materiaisN2.length === 0 && !isLoading && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Nenhum material liberado. Clique em "ADICIONAR" para liberar materiais.
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>

      <ConfirmDeleteModal
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false, tipo: "n1" })}
        onConfirm={handleRemoverMaterial}
        title="Remover Material"
        description="Tem certeza que deseja remover este material da liberação? Esta ação não pode ser desfeita."
      />

      <BackdropLoading isOpen={isLoading} />
    </DashboardStackCustom>
  );
};

